/**
 * Ports (M3 plan §1.2, §2.6.5; D-TBD 1): the only way M3 reaches M2 concepts. M3 builds beside M2 with its own
 * stand-ins (src/reach/standin/, R5); the M2 bridge (R22 or the M2 merge) swaps in M2's implementations.
 * FROZEN after R1: later tasks may add members, never rename or remove them.
 */
import type { Rng } from '../core/rng'
import type { StationSite } from '../galaxy/layout/types'
import type { ReachCore, SpawnShipSpec } from './runtime/types'
import type { PartId, ReachCommandResult } from './types'

export { createServiceRegistry } from './services'

/**
 * Credits, cargo and the scanner Mk (M2's career after the bridge). In play the port is `createReachBridge`'s wrapper
 * (`src/app/mode.ts`), typed `Required<CareerPort>` so it must forward every member, the optional ones included.
 */
export interface CareerPort {
  credits(): number
  earn(amount: number, reason: string): void
  spend(amount: number, reason: string): boolean // false = insufficient funds; nothing changes
  cargo(part: PartId): number
  cargoFreeT(): number
  addCargo(part: PartId, qty: number): number // quantity actually added
  removeCargo(part: PartId, qty: number): number // quantity actually removed
  scannerMk(): 1 | 2 | 3
  setScannerMk(mk: 1 | 2 | 3): void
  onPlayerDeath(): void // stand-in: the parts you carry are lost (M2's death rule)
  /**
   * Economy plan SY-2e: the strategic Parts-tab price of a relay part at `stationId`, or null when no strategic market
   * runs (the offline stand-in prices the part from `reach-standin.json partPrices`). Absent in the stand-in.
   */
  goodQuote?(stationId: string, part: PartId): PartQuote | null
  /**
   * SY-2e: buys `qty` relay parts out of the station's strategic stock at its price (a transfer, never a mint). The
   * result is the immediate answer; `settled` resolves once the strategic worker answered and the career is booked.
   */
  buyGood?(stationId: string, part: PartId, qty: number): SettlingResult
  /**
   * M6 fix wave (D-459 bug 1): the ship def id the player flies now (`career.ship.defId`). M6 P24b flies a boarded
   * ship on the start ship's def slot and keeps the slot's id, so the slot cannot tell which hull is flown. Absent in
   * the stand-in (the def slot's id stands); null while no career is bound. Allocation-free. Wrappers of this port
   * (`createReachBridge` in `src/app/mode.ts`) must forward it, or the wing never sees the boarded hull (D-466 follow-up).
   */
  flownShipDefId?(): string | null
}

/** A relay part's strategic price at one station (SY-2e). */
export interface PartQuote {
  /** Credits for one unit at the station's current price, or null when the station does not sell the part. */
  unit: number | null
  /** Whole units the station can sell now (its stock past reservations); 0 when it holds none. */
  stock: number
}

/** A Reach command result that also settles later (a strategic buy waits for the worker; SY-2e). */
export type SettlingResult = ReachCommandResult & { settled?: Promise<ReachCommandResult> }

/** The later result of a command that settles asynchronously (`SettlingResult`), or null. */
export function settledOf(result: ReachCommandResult): Promise<ReachCommandResult> | null {
  const settled = (result as SettlingResult).settled
  return settled instanceof Promise ? settled : null
}

/** Danger and interdiction groups (M2's encounter director after the bridge). */
export interface EncounterPort {
  dangerScale(systemIndex: number): number
  interdictionRatePerMin(systemIndex: number): number // 0 disables interdiction
  interdictorGroup(systemIndex: number, rng: Rng): readonly SpawnShipSpec[]
}

/** What a mounted station tab can reach. */
export interface PanelContext { core: ReachCore; station: StationSite; close(): void; refresh(): void }

/** One tab of the station services panel; any host (M3's hail panel now, M2's station screen later) can mount it. */
export interface StationServicePanel {
  id: string; title: string; order: number
  available(station: StationSite): boolean
  mount(el: HTMLElement, ctx: PanelContext): () => void // returns the unmount function
}

export interface StationServiceRegistry { register(panel: StationServicePanel): void; list(station: StationSite): StationServicePanel[] }
